import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Link } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MuiAlert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { handleUserLogin } from "../../redux/reducers/authReducer";
import { unwrapResult } from "@reduxjs/toolkit";
import { FaUser, FaLock } from "react-icons/fa";
import Logo from "../../assets/images/logo-red1.png";
import BackgroundLogin from "../../assets/images/g10.png";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Container = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background: radial-gradient(circle at 10% 20%, rgba(13, 244, 150, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 90% 80%, rgba(19, 202, 233, 0.08) 0%, transparent 40%),
              linear-gradient(135deg, #001c4c 0%, #0a2558 100%);
  flex-direction: ${({ lang }) => (lang === "ar" ? "row" : "row-reverse")};

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    overflow: auto;
  }
`;

const FormSection = styled.div`
  flex: 1.1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 20px;
    width: 100%;
    min-height: 100vh;
  }

  form {
    max-width: 440px;
    width: 100%;
    padding: 40px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
  }
`;

const InfoSection = styled.div`
  flex: 0.9;
  background: linear-gradient(135deg, rgba(19, 202, 233, 0.1) 0%, rgba(13, 244, 150, 0.1) 100%);
  border-right: ${({ lang }) => (lang === "ar" ? "none" : "1px solid rgba(255, 255, 255, 0.1)")};
  border-left: ${({ lang }) => (lang === "ar" ? "1px solid rgba(255, 255, 255, 0.1)" : "none")};
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 40px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    display: none;
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 24px;
`;

const Icon = styled.span`
  position: absolute;
  left: ${({ lang }) => (lang === "ar" ? "auto" : "16px")};
  right: ${({ lang }) => (lang === "ar" ? "16px" : "auto")};
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
`;

const InputField = styled.input`
  width: 100%;
  height: 52px;
  padding: ${({ lang }) => (lang === "ar" ? "12px 42px 12px 16px" : "12px 16px 12px 42px")};
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  outline: none;
  background-color: #f8fafc;
  transition: all 0.2s ease-in-out;
  color: #0f172a;

  &:focus {
    background-color: #ffffff;
    border-color: #13cae9;
    box-shadow: 0 0 0 4px rgba(19, 202, 233, 0.1);
  }

  &:focus ~ span {
    color: #13cae9;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  text-align: ${({ lang }) => (lang === "ar" ? "right" : "left")};
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 50px;
  margin-top: 10px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #13cae9 0%, #0de3b8 100%);
  color: white;
  font-size: 16px;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  box-shadow: 0 4px 12px rgba(13, 227, 184, 0.2);
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(13, 227, 184, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    box-shadow: none;
  }
`;

export default function UserLogin(props) {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setStep } = props;
  const { companyData } = useSelector((state) => ({
    companyData: state.Auth.companyData,
  }));
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("auth");
  const isRTL = i18n.language === "ar";
  const handleClose = () => {
    setError("");
  };

  const schema = yup.object({
    username: yup.string().required(t("Login.UserName_Error")),
    password: yup.string().required(t("Login.Password_Error")),
  });

  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      username: "",
      password: "",
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const submitForm = (data) => {
    dispatch(
      handleUserLogin({
        data: data,
        setLoading: setLoading,
      })
    )
      .then(unwrapResult)
      .catch(() => {
        setLoading(false);
        setError(t("Login.UserName_Error"));
      });
  };

  return (
    <Container lang={i18n.language}>
      <InfoSection lang={i18n.language}>
        <img src={Logo} alt="logo" style={{ width: "280px", marginBottom: "40px", filter: "brightness(0) invert(1)" }} />
        <img src={BackgroundLogin} alt="BackgroundLogin" style={{ width: "380px", opacity: 0.85 }} />
      </InfoSection>
      <FormSection>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(submitForm)}
          >
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
              <img src={Logo} alt="logo" style={{ width: "180px", display: "none" }} className="mobile-only-logo" />
            </div>
            
            <h2
              style={{
                color: "#0f172a",
                fontSize: "24px",
                fontWeight: 800,
                marginBottom: "8px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("Login.Login_Header")}
            </h2>
            <div
              style={{
                color: "#64748b",
                fontSize: "14px",
                fontWeight: 500,
                marginBottom: "30px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("Login.Login_Header_Text")}
            </div>

            <InputContainer>
              <Label lang={i18n.language}>{t("Login.UserName_Label")}</Label>
              <div style={{ position: "relative" }}>
                <InputField
                  lang={i18n.language}
                  type="text"
                  {...register("username")}
                  required
                  placeholder={t("Login.UserName_Label")}
                />
                <Icon lang={i18n.language}>
                  <FaUser />
                </Icon>
              </div>
            </InputContainer>
            {errors.username && (
              <p style={{ color: "#ef4444", fontSize: "13px", marginTop: "-16px", marginBottom: "16px", textAlign: isRTL ? "right" : "left" }}>
                {errors.username.message}
              </p>
            )}

            <InputContainer>
              <Label lang={i18n.language}>{t("Login.Password_Label")}</Label>
              <div style={{ position: "relative" }}>
                <InputField
                  lang={i18n.language}
                  type="password"
                  {...register("password")}
                  required
                  placeholder="••••••••"
                />
                <Icon lang={i18n.language}>
                  <FaLock />
                </Icon>
              </div>
            </InputContainer>
            {errors.password && (
              <p style={{ color: "#ef4444", fontSize: "13px", marginTop: "-16px", marginBottom: "16px", textAlign: isRTL ? "right" : "left" }}>
                {errors.password.message}
              </p>
            )}

            <SubmitButton type="submit" disabled={loading}>
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("Login.Login_Submit")
              )}
            </SubmitButton>
            <div
              style={{
                color: "#64748b",
                fontSize: "14px",
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              {t("Login.aliens_Description")}{" "}
              <span
                style={{
                  color: "#13cae9",
                  cursor: "pointer",
                  fontWeight: "bold",
                  marginLeft: "5px",
                  marginRight: "5px",
                  textDecoration: "underline",
                }}
                onClick={() => {
                  navigate("/signup");
                }}
              >
                {t("Login.signup")}
              </span>
            </div>
          </form>
        </FormProvider>
      </FormSection>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
